import React, { useEffect, useState } from 'react';
import Topbar from '../components/Topbar';
import ProposicaoCardNew from '../components/ProposicaoCardNew';
import JabutisHighlights from '../components/JabutisHighlights';
import { Proposicao, TipoProposicao, StatusProposicao } from '../types';
import './DashboardNew.css';

interface ProposicaoWithJabuti extends Proposicao {
  jabuti?: any;
}

const TIPOS: TipoProposicao[] = ['PEC', 'PL', 'PLP', 'MPV', 'PDL'];
const STATUSES: { value: StatusProposicao; label: string }[] = [
  { value: 'tramitando', label: 'Tramitando' },
  { value: 'urgente', label: 'Urgente' },
  { value: 'aprovado', label: 'Aprovado' },
  { value: 'arquivado', label: 'Arquivado' },
  { value: 'vetado', label: 'Vetado' },
];

export default function DashboardNew() {
  const [proposicoes, setProposicoes] = useState<ProposicaoWithJabuti[]>([]);
  const [jabutisSummary, setJabutisSummary] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [tipo, setTipo] = useState<TipoProposicao | 'todas'>('todas');
  const [status, setStatus] = useState<StatusProposicao | 'todos'>('todos');
  const [showJabutisOnly, setShowJabutisOnly] = useState(false);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [propsRes, sumRes] = await Promise.all([
          fetch('/api/proposicoes?limit=100'),
          fetch('/api/proposicoes/jabutis/summary?limit=8'),
        ]);
        const propsData = await propsRes.json();
        const sumData = await sumRes.json();
        setProposicoes(propsData.results || []);
        setJabutisSummary(sumData.summary || null);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  const filtered = proposicoes.filter((p) => {
    const tipoMatch = tipo === 'todas' || p.tipo === tipo;
    const statusMatch = status === 'todos' || p.status === status;
    const jabutiMatch = !showJabutisOnly || ((p as any).jabuti && (p as any).jabuti.level !== 'low');
    return tipoMatch && statusMatch && jabutiMatch;
  });

  const jabutiCount = proposicoes.filter((p) => (p as any).jabuti && (p as any).jabuti.level !== 'low').length;
  const tipoCount = (t: TipoProposicao) => proposicoes.filter((p) => p.tipo === t).length;
  const statusCount = (s: StatusProposicao) => proposicoes.filter((p) => p.status === s).length;

  return (
    <div className="dashboard-container">
      <Topbar title="Monitoramento Legislativo" />
      <div className="dashboard-main">
        <div className="dashboard-header">
          <div>
            <p className="subtitle">
              {filtered.length} proposiç{filtered.length !== 1 ? 'ões' : 'ão'}
              {filtered.length !== proposicoes.length ? ` de ${proposicoes.length}` : ' carregadas'}
            </p>
          </div>
        </div>

        <div className="filters">
          <button
            className={`filter-btn ${tipo === 'todas' ? 'active' : ''}`}
            onClick={() => setTipo('todas')}
          >
            Todas ({proposicoes.length})
          </button>
          {TIPOS.filter((t) => tipoCount(t) > 0).map((t) => (
            <button
              key={t}
              className={`filter-btn ${tipo === t ? 'active' : ''}`}
              onClick={() => setTipo(t)}
            >
              {t} ({tipoCount(t)})
            </button>
          ))}

          <span className="filter-divider" aria-hidden />

          <button
            className={`filter-btn ${status === 'todos' ? 'active' : ''}`}
            onClick={() => setStatus('todos')}
          >
            Todos status
          </button>
          {STATUSES.filter((s) => statusCount(s.value) > 0).map((s) => (
            <button
              key={s.value}
              className={`filter-btn ${status === s.value ? 'active' : ''}`}
              onClick={() => setStatus(s.value)}
            >
              {s.label} ({statusCount(s.value)})
            </button>
          ))}

          <button
            className={`filter-btn jabuti-filter ${showJabutisOnly ? 'active' : ''}`}
            onClick={() => setShowJabutisOnly(!showJabutisOnly)}
            title="Filtrar por proposições com sinais de atenção (IAL ≥ médio)"
          >
            ◈ Atenção legislativa ({jabutiCount})
          </button>
        </div>

        {jabutisSummary?.highlights && (
          <JabutisHighlights highlights={jabutisSummary.highlights} limit={6} />
        )}

        {loading ? (
          <div className="loading">Carregando...</div>
        ) : filtered.length === 0 ? (
          <div className="empty">Nenhuma proposição encontrada</div>
        ) : (
          <div className="proposicoes-grid">
            {filtered.map((prop) => (
              <ProposicaoCardNew key={prop.id} proposicao={prop} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
